import React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { AppUI } from '@seedlang/state';
import {
  Box,
  Center,
  Group,
  Image,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import PageTitle from 'components/common/page_title';
import NoAccessButton from 'components/worksheet/no_access_button';
import PodcastAudioPlayer from 'components/worksheet/podcast_audio_player';
import withRouteParams from 'utils/with_route_params';

@observer
class PodcastEpisodeShow extends React.Component {
  @observable isLoading = true;
  @observable loadFailed = false;

  componentDidMount() {
    this.getPodcastEpisode();
  }

  componentDidUpdate(prevProps) {
    if (this.props.routeParams.podcastEpisodeId !== prevProps.routeParams.podcastEpisodeId) {
      this.getPodcastEpisode();
    }
  }

  componentWillUnmount() {
    AppUI.podcastEpisodeStore.clearShowData();
  }

  getPodcastEpisode = () => {
    this.isLoading = true;
    this.loadFailed = false;
    AppUI.podcastEpisodeStore.clearShowData();
    AppUI.podcastEpisodeStore.getShow(
      { ids: { podcastEpisodeId: this.props.routeParams.podcastEpisodeId } },
      null,
      {
        successCallback: () => this.isLoading = false,
        onRequestError: this.handleRequestError,
      },
    );
  }

  handleRequestError = error => {
    this.isLoading = false;

    if (error?.status === 404) {
      AppUI.routeToHome();
      return;
    }

    this.loadFailed = true;
  }

  renderValue(value) {
    return value || '—';
  }

  render() {
    const store = AppUI.podcastEpisodeStore;
    const episode = store.showData;

    if (this.isLoading || store.hasOutstandingRequests) {
      return (
        <Center py='xl'>
          <Loader color={AppUI.site.accentColor} />
        </Center>
      );
    }

    if (this.loadFailed) {
      return (
        <Paper p='xl' radius='md'>
          <Text>Sorry, we couldn&apos;t load this podcast episode.</Text>
        </Paper>
      );
    }

    if (!episode.accessStatus?.hasAccess) {
      return (
        <Paper p='xl' radius='md'>
          <NoAccessButton
            margin='0'
            reason={episode.accessStatus?.reason}
          />
        </Paper>
      );
    }

    return (
      <Paper p={{ base: 'md', sm: 'xl' }} radius='md'>
        <PageTitle>{episode.title}</PageTitle>
        <Stack gap='xl'>
          <Stack gap='lg'>
            <Group align='center' gap='lg' wrap='nowrap'>
              {
                episode.imageUrl &&
                  <Image
                    alt={`${episode.title || 'Podcast'} cover`}
                    fit='cover'
                    h={64}
                    radius='lg'
                    src={episode.imageUrl}
                    style={{ borderRadius: '12px' }}
                    w={64}
                  />
              }
              <Stack gap={2}>
                {
                  episode.podcast?.title &&
                    <Text c='dimmed' fw={600} size='xs'>{episode.podcast.title}</Text>
                }
                <Title order={1} size='h2'>{this.renderValue(episode.title)}</Title>
              </Stack>
            </Group>
            {
              episode.subtitle &&
                <Text c='dimmed' size='md'>{episode.subtitle}</Text>
            }
          </Stack>
          {
            episode.mp3?.url &&
              <Box w='100%'>
                <PodcastAudioPlayer podcastEpisode={episode} />
              </Box>
          }
          {
            episode.mailerliteWebViewUrl &&
              <Box
                component='iframe'
                h={{ base: 600, md: 800 }}
                loading='lazy'
                src={episode.mailerliteWebViewUrl}
                style={{ border: 0, borderRadius: '12px' }}
                title={`${episode.title || 'Podcast episode'} newsletter`}
                w='100%'
              />
          }
        </Stack>
      </Paper>
    );
  }
}

export default withRouteParams(ErrorBoundary(PodcastEpisodeShow));
